/**
 * SUB-2.10 · Ordering a flat tenant list into the picker's hierarchy.
 *
 * FR17: each parent account is followed by its sub-accounts, visually indented,
 * with standalone accounts at the top level, so a superadmin can tell parent from
 * sub from standalone without opening anything.
 *
 * Presentation only. `tenants.parent_tenant_id` already arrives in the picker's
 * existing `select('*')` (added by migration 20260812e), so this adds no query
 * surface, no RPC and no policy dependency.
 *
 * Three deliberate decisions, each protecting FR16/NFR1 ("superadmin behaviour is
 * unchanged and they retain access to all accounts"):
 *
 * 1. NO SORTING. The caller's order is authoritative. The picker's list is ordered
 *    by the database (`.order('company_name')`, database collation) and appended to
 *    by TenantContext.addTenant (`localeCompare`). Introducing a third comparison
 *    here would reorder the live picker: the two collations already disagree on the
 *    live data, where the database puts "Testing" before "TEST Scope 98e32ff0" and
 *    localeCompare does not. Top-level rows keep their relative input order, and so
 *    do the children under each parent.
 *
 * 2. EVERY INPUT ROW IS EMITTED EXACTLY ONCE. A sub-account is nested only when its
 *    parent is present in the same list; otherwise it is emitted at the top level.
 *    This is not defensiveness, it is the live case: TenantContext.fetchUserTenants
 *    filters archived tenants out, so an archived parent with live children yields
 *    children whose `parent_tenant_id` names a tenant absent from the array. Nesting
 *    them under a parent that is not there would drop them from the picker and make
 *    real accounts unreachable, which is exactly what NFR1 forbids.
 *
 * 3. NO RECURSION. The hierarchy is one level deep by design (FR6), enforced in the
 *    database by SUB-2.3. A single grouping pass therefore cannot loop, so a cycle
 *    that somehow reached the browser degrades to a flat list instead of hanging the
 *    sidebar.
 */

/** Everything this module needs from a tenant. Any wider object passes through. */
export interface TenantHierarchyInput {
  tenant_id: string;
  parent_tenant_id?: string | null;
}

/** What the row is, for FR17's "at a glance" distinction. */
export type TenantHierarchyKind = 'parent' | 'sub' | 'standalone';

export interface TenantHierarchyRow<T extends TenantHierarchyInput> {
  /** The original object, by reference. Callers keep every field they passed in. */
  tenant: T;
  /**
   * 0 for parents and standalone accounts, 1 for sub-accounts. Drives the indent.
   *
   * ⚠️ `kind === 'sub'` does NOT imply `depth === 1`. An orphan — a sub-account
   * whose parent is absent from this list, which happens whenever the parent is
   * archived — keeps its identity and is emitted at depth 0 so it stays reachable.
   */
  depth: 0 | 1;
  kind: TenantHierarchyKind;
  /**
   * Number of sub-accounts this row has IN THIS LIST.
   *
   * ⚠️ NOT a fact about the account. The list is archived-filtered and scoped to
   * what the viewer can reach, so this can read lower than the account's real
   * child count and WILL disagree with `formatSubAccountUsage(usage)` on the
   * sub-accounts page, which comes from a server-side count under a different
   * rule. Two authoritative-looking numbers for the same quantity, by
   * construction. Treat this one as a property of the picker, not of the tenant.
   */
  subAccountCount: number;
}

/**
 * Order a flat tenant list so each parent is followed by its sub-accounts.
 *
 * Returns one row per DISTINCT input tenant, in every case.
 *
 * ⚠️ "Distinct" is load-bearing. The emitted-once guard means a repeated
 * `tenant_id` yields one row, not two, so `rows.length < tenants.length` for a
 * list containing a duplicate. The input is the union of the `users.tenant_id`
 * and `user_roles.tenant_id` paths and nothing upstream guarantees uniqueness,
 * so the duplicate is deduplicated here, deliberately, rather than silently
 * dropped further down.
 */
export function buildTenantHierarchy<T extends TenantHierarchyInput>(
  tenants: T[],
): TenantHierarchyRow<T>[] {
  if (!tenants || tenants.length === 0) return [];

  // Dedupe first, so every count and every guarantee below is over one row per id.
  const byId = new Map<string, T>();
  tenants.forEach((tenant) => {
    if (!byId.has(tenant.tenant_id)) byId.set(tenant.tenant_id, tenant);
  });
  tenants = Array.from(byId.values());

  const present = new Set(tenants.map((tenant) => tenant.tenant_id));

  // A row nests only if it names a DIFFERENT tenant that is in this same list.
  // Self-parenting and absent parents both fall through to the top level.
  const parentIdOf = (tenant: T): string | null => {
    const parentId = tenant.parent_tenant_id;
    if (!parentId) return null;
    if (parentId === tenant.tenant_id) return null;
    if (!present.has(parentId)) return null;
    return parentId;
  };

  const childrenByParent = new Map<string, T[]>();
  tenants.forEach((tenant) => {
    const parentId = parentIdOf(tenant);
    if (!parentId) return;
    const siblings = childrenByParent.get(parentId);
    if (siblings) siblings.push(tenant);
    else childrenByParent.set(parentId, [tenant]);
  });

  const rows: TenantHierarchyRow<T>[] = [];
  const emitted = new Set<string>();

  // How many sub-accounts this row has IN THIS LIST, whether or not they are
  // emitted beneath it. Reading it off the emitted children made a middle row of
  // an illegal three-level chain report zero while having one.
  const childCountOf = (tenantId: string) =>
    (childrenByParent.get(tenantId) ?? []).length;

  /**
   * ⚠️ `kind` ASKS "DOES THIS ROW NAME A PARENT", NOT "DID WE EMIT ONE".
   *
   * It used to be `children.length > 0 ? 'parent' : 'standalone'`, so a row whose
   * `parent_tenant_id` is set but whose parent is absent from this list — the
   * documented live case, an archived parent filtered out by `fetchUserTenants` —
   * came out `standalone`. TenantRowLabel drops the `└` glyph and the
   * "Sub-account ·" marker for standalone, so the picker actively told the viewer
   * that a managed client account was an independent company. Depth still falls
   * back to 0 so the row stays reachable; only the identity is preserved.
   */
  const kindOf = (tenant: T, childCount: number): TenantHierarchyRow<T>['kind'] => {
    if (childCount > 0) return 'parent';
    const rawParent = tenant.parent_tenant_id;
    if (rawParent && rawParent !== tenant.tenant_id) return 'sub';
    return 'standalone';
  };

  const emitTopLevel = (tenant: T) => {
    if (emitted.has(tenant.tenant_id)) return;
    emitted.add(tenant.tenant_id);

    const children = (childrenByParent.get(tenant.tenant_id) ?? []).filter(
      (child) => !emitted.has(child.tenant_id),
    );

    const childCount = childCountOf(tenant.tenant_id);

    rows.push({
      tenant,
      depth: 0,
      kind: kindOf(tenant, childCount),
      subAccountCount: childCount,
    });

    children.forEach((child) => {
      emitted.add(child.tenant_id);
      rows.push({
        tenant: child,
        depth: 1,
        kind: 'sub',
        subAccountCount: childCountOf(child.tenant_id),
      });
    });
  };

  // Pass 1: the real shape. Anything with no resolvable parent is a top-level row,
  // and it brings its children up with it.
  tenants.forEach((tenant) => {
    if (parentIdOf(tenant)) return;
    emitTopLevel(tenant);
  });

  // Pass 2: the exactly-once guarantee, in code rather than in reasoning. A mutual
  // cycle (A parents B, B parents A) leaves every row in it claimed as somebody's
  // child and owned by no top-level row, so pass 1 alone emits neither and both
  // accounts disappear from the picker. Unreachable through the app, and NFR1 is
  // the requirement that must not depend on that being true.
  tenants.forEach(emitTopLevel);

  return rows;
}
